import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { api, API } from "@/lib/api";
import { makeSseParser } from "@/lib/sse";

// Colors keyed to score bands. Same thresholds the interrogator uses:
// ≥65 = advance; ≥80 = strong signal; <50 = red.
function scoreBand(n) {
  if (n == null) return { label: "—", tone: "slate", pct: 0 };
  if (n >= 80) return { label: "Strong", tone: "emerald", pct: n };
  if (n >= 65) return { label: "Clear", tone: "blue", pct: n };
  if (n >= 50) return { label: "Weak", tone: "amber", pct: n };
  return { label: "Failing", tone: "rose", pct: n };
}

const TONE_CLASSES = {
  slate: { bar: "bg-slate-300", chip: "bg-slate-100 text-slate-600 border-slate-200" },
  emerald: { bar: "bg-emerald-500", chip: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  blue: { bar: "bg-blue-500", chip: "bg-blue-50 text-blue-700 border-blue-200" },
  amber: { bar: "bg-amber-500", chip: "bg-amber-50 text-amber-800 border-amber-200" },
  rose: { bar: "bg-rose-500", chip: "bg-rose-50 text-rose-700 border-rose-200" },
};

const SEVERITY_TONES = {
  high: "bg-rose-50 border-rose-300 text-rose-800",
  medium: "bg-amber-50 border-amber-300 text-amber-800",
  low: "bg-slate-50 border-slate-200 text-slate-700",
};

function ScoreGauge({ ring, score }) {
  const band = scoreBand(score?.score);
  const tone = TONE_CLASSES[band.tone];
  return (
    <div className="card-flat" data-testid={`gauge-ring-${ring}`}>
      <div className="mono-caps text-[0.65rem] text-slate-500 mb-2">Ring {ring}</div>
      <div className="flex items-baseline gap-2 mb-3">
        <span className="font-display text-4xl leading-none">
          {score?.score ?? "—"}
        </span>
        <span className="text-slate-400 text-sm">/100</span>
      </div>
      <div className="h-2 bg-slate-100 rounded overflow-hidden mb-3">
        <div
          className={`h-full ${tone.bar} transition-all`}
          style={{ width: `${band.pct}%` }}
        />
      </div>
      <span className={`mono-caps text-[0.6rem] inline-block px-2 py-0.5 rounded border ${tone.chip}`}>
        {band.label} · signal {score?.signal || "—"}
      </span>
    </div>
  );
}

function ReopenRingButton({ ring, ideaId, currentActive, onReopened }) {
  const [busy, setBusy] = useState(false);
  const active = currentActive === ring;
  async function go() {
    setBusy(true);
    try {
      await api.post(`/ideas/${ideaId}/reopen-ring`, { ring });
      toast.success(`Ring ${ring} reopened — head to the chat to dial in your score`, {
        description: "Your Ring 4 progress is preserved.",
      });
      onReopened?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not reopen ring.");
    } finally {
      setBusy(false);
    }
  }
  return (
    <button
      className={`mono-caps text-[0.65rem] px-3 py-1.5 rounded border transition-colors ${
        active
          ? "bg-blue-100 border-blue-300 text-blue-800"
          : "border-slate-200 text-slate-600 hover:bg-slate-50"
      }`}
      onClick={go}
      disabled={busy || active}
      data-testid={`reopen-ring-${ring}`}
    >
      {active ? `Ring ${ring} · dial-in mode` : busy ? "…" : `Improve Ring ${ring}`}
    </button>
  );
}

export default function ExecutiveDashboard() {
  const { id } = useParams();
  const nav = useNavigate();
  const [idea, setIdea] = useState(null);
  const [dashboard, setDashboard] = useState(null);
  const [scores, setScores] = useState({});
  const [stale, setStale] = useState(false);
  const [loading, setLoading] = useState(true);
  const [regenerating, setRegenerating] = useState(false);
  const [regenElapsed, setRegenElapsed] = useState(0);
  const [error, setError] = useState(null);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const [ideaResp, dashResp] = await Promise.all([
        api.get(`/ideas/${id}`),
        api.get(`/ideas/${id}/dashboard`),
      ]);
      setIdea(ideaResp.data.idea);
      setDashboard(dashResp.data.dashboard);
      setScores(dashResp.data.scores || {});
      // Auto Dashboard Refresh (v14f): server is source of truth for
      // staleness. Only show the pulse when the dashboard EXISTS AND the
      // server says the underlying scores have changed since it was
      // generated — an empty dashboard has its own "regenerate to
      // synthesize" empty state, no need to double up with a pulse.
      setStale(Boolean(dashResp.data.dashboard_stale && dashResp.data.dashboard));
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not load dashboard.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  async function regenerate() {
    // Use the SSE endpoint — the LLM synthesis takes 30-70s which exceeds
    // Cloudflare's 60s edge timeout for regular POSTs. SSE keeps the
    // connection alive with heartbeats until the `done` frame arrives.
    setRegenerating(true);
    setRegenElapsed(0);
    try {
      const resp = await fetch(`${API}/ideas/${id}/dashboard/refresh/stream`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
      });
      if (!resp.ok) {
        const detail = await resp.text();
        throw new Error(detail || `HTTP ${resp.status}`);
      }
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      const parse = makeSseParser();
      let doneData = null;
      let sawError = null;
      // Manual read loop — same pattern as the streaming ring / panel readers.
      // Exits when the body closes or a done/error frame arrives.
      // eslint-disable-next-line no-constant-condition
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        const chunk = decoder.decode(value, { stream: true });
        for (const frame of parse(chunk)) {
          if (frame.event === "heartbeat") {
            setRegenElapsed(frame.data?.elapsed_s || 0);
          } else if (frame.event === "done") {
            doneData = frame.data;
          } else if (frame.event === "error") {
            sawError = frame.data?.detail || "Synthesis failed.";
          }
        }
        if (doneData || sawError) break;
      }
      reader.cancel().catch(() => {});
      if (sawError) throw new Error(sawError);
      if (!doneData) throw new Error("Synthesis ended without a result.");
      setDashboard(doneData.dashboard);
      setScores(doneData.scores || {});
      // Server clears dashboard_stale on successful synthesis, so on the
      // next load() the pulse won't show. Reflect that locally too so the
      // pulse disappears immediately without waiting for a page reload.
      setStale(false);
      toast.success("Dashboard synthesis complete");
    } catch (e) {
      toast.error(e?.message || "Regeneration failed.");
    } finally {
      setRegenerating(false);
    }
  }

  async function exportMarkdown() {
    try {
      const resp = await fetch(`${API}/ideas/${id}/dashboard/markdown`, { credentials: "include" });
      if (!resp.ok) throw new Error(await resp.text());
      const blob = await resp.blob();
      const dlUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = dlUrl;
      a.download = `executive-dashboard-${(idea?.title || "idea").slice(0, 40).replace(/\s+/g, "-").toLowerCase()}.md`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(dlUrl);
      toast.success("Markdown exported — feed this into AnswerWiki");
    } catch (e) {
      toast.error("Export failed: " + e.message);
    }
  }

  if (loading) return <div className="p-14 text-slate-500 mono-caps">Loading…</div>;
  if (error) {
    return (
      <div className="min-h-screen p-14 max-w-3xl mx-auto">
        <button className="mono-caps hover:text-slate-900 mb-8" onClick={() => nav(`/ideas/${id}`)} data-testid="dashboard-back">
          ← Chat
        </button>
        <div className="card-flat border border-rose-200 bg-rose-50 text-rose-900" data-testid="dashboard-error">
          {error}
        </div>
      </div>
    );
  }

  const risks = dashboard?.risks || [];
  const hyps = dashboard?.hypotheses || [];
  const p30 = dashboard?.priorities_30 || [];
  const p60 = dashboard?.priorities_60 || [];
  const p90 = dashboard?.priorities_90 || [];
  const generatedAt = dashboard?.generated_at;

  return (
    <div className="min-h-screen">
      <div className="max-w-[1120px] mx-auto px-8 py-14">
        <button
          className="mono-caps hover:text-slate-900 transition-colors mb-10"
          onClick={() => nav(`/ideas/${id}`)}
          data-testid="dashboard-back"
        >
          ← Chat
        </button>

        <div className="flex items-start justify-between mb-8 gap-8 flex-wrap">
          <div className="min-w-0">
            <div className="mono-caps mb-3 text-blue-700">Executive Dashboard · Living document</div>
            <h1 className="font-display text-4xl sm:text-5xl mb-3">{idea?.title}</h1>
            {generatedAt && (
              <div className="mono-caps text-[0.65rem] text-slate-400" data-testid="dashboard-generated-at">
                Last synthesized {new Date(generatedAt).toLocaleString()}
              </div>
            )}
          </div>
          <div className="flex flex-wrap gap-3 items-center">
            {stale && (
              <div
                className="mono-caps text-[0.65rem] text-amber-800 bg-amber-50 border border-amber-200 rounded px-3 py-1.5"
                data-testid="dashboard-stale-caption"
              >
                Scores changed · Regenerate for current verdict
              </div>
            )}
            <button
              className={`btn-primary ${stale ? "dashboard-stale-pulse" : ""}`}
              onClick={regenerate}
              disabled={regenerating}
              data-testid="dashboard-regenerate"
            >
              {regenerating
                ? regenElapsed > 0
                  ? `Synthesizing… (${regenElapsed}s)`
                  : "Synthesizing…"
                : dashboard
                ? "Regenerate"
                : "Generate now"}
            </button>
            {dashboard && (
              <button
                className="btn-secondary"
                onClick={exportMarkdown}
                data-testid="dashboard-export-md"
              >
                Export as .md
              </button>
            )}
            <button
              className="btn-secondary"
              onClick={() => nav(`/ideas/${id}/documents`)}
              data-testid="dashboard-open-docs"
            >
              Download briefs
            </button>
          </div>
        </div>

        {/* Scores + reopen a ring */}
        <section className="mb-14" data-testid="dashboard-scores-section">
          <div className="mono-caps text-slate-500 mb-4">Scores</div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-5">
            <ScoreGauge ring={1} score={scores.ring1} />
            <ScoreGauge ring={2} score={scores.ring2} />
            <ScoreGauge ring={3} score={scores.ring3} />
          </div>
          <div className="text-slate-500 text-sm mb-3">
            Not satisfied with a score? Dial it in — your Ring 4 progress is preserved.
          </div>
          <div className="flex gap-2 flex-wrap">
            <ReopenRingButton ring={1} ideaId={id} currentActive={idea?.active_ring} onReopened={load} />
            <ReopenRingButton ring={2} ideaId={id} currentActive={idea?.active_ring} onReopened={load} />
            <ReopenRingButton ring={3} ideaId={id} currentActive={idea?.active_ring} onReopened={load} />
            {idea?.active_ring && (
              <button
                className="mono-caps text-[0.65rem] px-3 py-1.5 rounded border border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                onClick={async () => {
                  await api.post(`/ideas/${id}/resume-frontier`);
                  toast.success("Back to the frontier");
                  load();
                }}
                data-testid="dashboard-resume-frontier"
              >
                Resume Ring 4
              </button>
            )}
          </div>
        </section>

        {!dashboard && (
          <div
            className="card-flat text-center py-14 border-dashed border-2 border-slate-200"
            data-testid="dashboard-empty"
          >
            <div className="mono-caps text-slate-500 mb-3">No synthesis yet</div>
            <div className="text-slate-600 mb-6">
              Hit <span className="font-semibold">Generate now</span> to have Claude extract every risk,
              open hypothesis, and priority from your Ring 1–3 transcripts.
            </div>
            <button className="btn-primary" onClick={regenerate} disabled={regenerating} data-testid="dashboard-empty-generate">
              {regenerating ? "Synthesizing…" : "Generate now"}
            </button>
          </div>
        )}

        {dashboard && (
          <>
            {/* Risks */}
            <section className="mb-14" data-testid="dashboard-risks-section">
              <div className="flex items-baseline justify-between mb-5">
                <h2 className="font-display text-2xl">Risk register</h2>
                <span className="mono-caps text-slate-500 text-[0.65rem]">
                  {risks.length} risk{risks.length === 1 ? "" : "s"} surfaced
                </span>
              </div>
              {risks.length === 0 && (
                <div className="text-slate-500 text-sm">No risks surfaced across Rings 1–3.</div>
              )}
              <div className="space-y-4">
                {risks.map((r, i) => (
                  <div
                    key={i}
                    className={`rounded-lg border p-5 ${SEVERITY_TONES[r.severity] || SEVERITY_TONES.low}`}
                    data-testid={`risk-item-${i}`}
                  >
                    <div className="flex items-start justify-between gap-4 mb-2">
                      <h3 className="font-semibold text-base leading-snug">
                        {i + 1}. {r.title}
                      </h3>
                      <div className="mono-caps text-[0.55rem] shrink-0 flex gap-2">
                        <span className="px-2 py-0.5 rounded bg-white/60 border border-current/20">
                          Ring {r.source_ring}
                        </span>
                        <span className="px-2 py-0.5 rounded bg-white/60 border border-current/20">
                          {r.severity || "—"}
                        </span>
                      </div>
                    </div>
                    <p className="text-sm mb-3 leading-relaxed">{r.detail}</p>
                    <div className="text-sm">
                      <span className="mono-caps text-[0.6rem] opacity-70">Mitigation · </span>
                      {r.mitigation}
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* Hypotheses */}
            <section className="mb-14" data-testid="dashboard-hypotheses-section">
              <div className="flex items-baseline justify-between mb-5">
                <h2 className="font-display text-2xl">Open hypotheses</h2>
                <span className="mono-caps text-slate-500 text-[0.65rem]">
                  {hyps.length} to validate
                </span>
              </div>
              {hyps.length === 0 && (
                <div className="text-slate-500 text-sm">No open hypotheses.</div>
              )}
              <div className="space-y-4">
                {hyps.map((h, i) => (
                  <div key={i} className="card-flat" data-testid={`hypothesis-item-${i}`}>
                    <div className="flex items-start justify-between gap-4 mb-2">
                      <h3 className="font-semibold text-base leading-snug">
                        {i + 1}. {h.title}
                      </h3>
                      <span className="mono-caps text-[0.55rem] shrink-0 px-2 py-0.5 rounded bg-slate-100 text-slate-600">
                        Ring {h.source_ring}
                      </span>
                    </div>
                    <div className="text-sm text-slate-700 mb-2">
                      <span className="mono-caps text-[0.6rem] text-slate-500">Why open · </span>
                      {h.why_open}
                    </div>
                    <div className="text-sm text-slate-700">
                      <span className="mono-caps text-[0.6rem] text-slate-500">How to test · </span>
                      {h.how_to_test}
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* 30/60/90 priorities */}
            <section className="mb-14" data-testid="dashboard-priorities-section">
              <h2 className="font-display text-2xl mb-5">30 · 60 · 90 day priorities</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {[
                  { label: "30 days", items: p30, testid: "priorities-30" },
                  { label: "60 days", items: p60, testid: "priorities-60" },
                  { label: "90 days", items: p90, testid: "priorities-90" },
                ].map((col) => (
                  <div key={col.label} className="card-flat" data-testid={col.testid}>
                    <div className="mono-caps text-blue-700 mb-3">{col.label}</div>
                    {col.items.length === 0 && <div className="text-slate-400 text-sm">None</div>}
                    <ul className="space-y-2">
                      {col.items.map((item, i) => (
                        <li key={i} className="text-sm text-slate-700 flex gap-2">
                          <span className="text-slate-300 shrink-0">·</span>
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </section>
          </>
        )}
      </div>
    </div>
  );
}
